import React from 'react';
import { motion } from 'framer-motion';
import './css/BusinessVertical.css';
import SEO from '../components/SEO';
import { serviceSchema, productSchema } from '../components/StructuredData';

const EcoFriendlySolutions = () => {
  const structuredData = [
    serviceSchema(
      "Eco-Friendly & Sustainable Chemical Solutions",
      "Eco alternatives for OEMs & manufacturers — replacing hazardous solvents, acids, and alkalis with high-performance, green parts cleaning & surface finishing chemicals.",
      "Sustainable Chemical Solutions"
    ),
    productSchema(
      "Green Industrial Cleaners",
      "VOC-free, biodegradable cleaning solutions for industrial applications",
      "Eco-Friendly Solutions"
    )
  ];

  return (
    <>
      <SEO
        title="Eco-Friendly Solutions - Sustainable Chemical Alternatives | Nimble Group"
        description="Leading provider of eco-friendly chemical solutions for OEMs and manufacturers. 100% VOC-free alternatives replacing hazardous solvents with high-performance green chemistry solutions."
        keywords="eco-friendly solutions, sustainable chemicals, green chemistry, VOC-free solutions, biodegradable cleaners, sustainable manufacturing, green alternatives, Alternative for Acetone, Alternative for Kerosene, Alternative for methanol, Alternative for petroleum solvents, Alternative for TCE, Alternative for Thinner, Alternative for Tri Chloroethylene, Biodegradable cleaners, customized eco cleaning solutions, eco alternative to methanol in cleaning, Eco cleaners, Eco degreasers, Eco friendly alternative for Kerosene, Eco friendly alternative for TCE, eco friendly alternative to acetone, eco friendly alternative to isopropyl alcohol (IPA), eco friendly alternative to petroleum solvents, Eco friendly cleaners, eco friendly cleaners for automotive parts, Eco friendly degreasers, eco friendly degreasing for metal parts, eco friendly industrial cleaning solutions, eco friendly industrial degreasing solutions, eco friendly industrial parts cleaners, Eco friendly substitute  for Kerosene, eco friendly substitute for chlorinated solvents, Eco friendly substitute for TCE, Eco friendly thinner alternative, Eco friendly thinner substitute, Eco friendly ultrasonic cleaners, Eco solutions, eco solvent for cleaning, Eco TCE, Eco-friendly chemical alternatives, eco-friendly chemicals, environment  friendly cleaners, green alternative to kerosene solvents, green parts cleaning solutions, industrial cleaning solution providers Mumbai, industrial parts cleaning solutions India, Kerosene Alternative, Kerosene Substitute, Nimble eco solutions, Nimble Technologies, Nimble technologies sustainable solutions, Non flammable cleaners, Non hazardous chemicals for cleaning, non hazardous parts cleaning chemicals, non toxic alternative to industrial acids and alkalis, non toxic industrial cleaning solutions, ODC free cleaners, safe alternatives for industrial cleaning solvents, Solvent Cleaners, Substitute for  Acetone, Substitute for Kerosene, Substitute for solvents, Substitute for TCE, Substitute for tri Chloro Ethylene, substitute for trichloroethylene (TCE), sustainable industrial degreasers, TCE Alternative, TCE Substitute, Thinner Alternative, Tri Chloroethylene Substitute, Ultrasonic Cleaners, ultrasonic eco friendly parts cleaners, Waterless Hand Cleaner"
        canonical="/business/eco-friendly-solutions"
        structuredData={structuredData}
      />
      <EcoFriendlySolutionsContent />
    </>
  );
};

const EcoFriendlySolutionsContent = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Key stats with animation
  const stats = [
    { value: '100%', label: 'Eco-Friendly Solutions' },
    { value: '0', label: 'VOC Emissions' },
    { value: '50%', label: 'Reduced Water Usage' },
    { value: '12', label: 'Green Chemistry Principles' }
  ];

  return (
    <div className="business-vertical-page">
      <motion.div 
        className="business-vertical-hero eco-friendly-solutions"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <h1>Advanced Eco friendly & Sustainable Solutions</h1>
          <h2 className="tagline">♻️ Revolutionizing Industrial Parts Cleaning with Eco-Safe Alternatives</h2>
          <p>Experience superior cleaning performance by compiling EHS regulations & Sustainability Principles</p>
        </div>
      </motion.div>

      <section className="business-vertical-content">
        <div className="container">
          <motion.div
            className="stats-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {stats.map((stat, index) => (
              <motion.div 
                className="stat-box"
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
              >
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={fadeIn.transition}
          >
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">♻️</span>
              </div>
              <div className="section-content">
                <p>At Nimble Gogreen, we believe that industrial cleaning should never compromise human safety or environmental health. Our mission is to deliver powerful, sustainable, and safe cleaning solutions that outperform conventional hazardous chemicals without harming your workforce, equipment, or the planet.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
          >
            <div className="eco-friendly-header">
              <h3>🌱 Our Eco-Friendly Product Portfolio</h3>
              <p>Discover our comprehensive range of sustainable cleaning solutions designed to revolutionize industrial processes while protecting our planet.</p>
            </div>
            <motion.div 
              className="product-portfolio"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <motion.div 
                className="portfolio-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h4>🧪 Alternatives to Carcinogenic Chlorinated Solvents</h4>
                <p>Replace Trichloroethylene (TCE) and similar solvents with our high-performance, eco-friendly degreasers and cleaners, offering superior results without carcinogenic risks.</p>
              </motion.div>
              
              <motion.div 
                className="portfolio-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h4>🌿 Biodegradable Replacements for Petroleum Solvents</h4>
                <p>Shift from petroleum-based solvents to our readily biodegradable solutions that deliver exceptional cleaning power while naturally breaking down to reduce environmental impact.</p>
              </motion.div>
              
              <motion.div 
                className="portfolio-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h4>🌱 Green Substitutes for Chemicals</h4>
                <p>Eliminate flammability and VOC concerns by replacing Isopropyl Alcohol, Methanol, and other alcohol-based cleaners with our safe, high-performance green alternatives.</p>
              </motion.div>
              
              <motion.div 
                className="portfolio-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h4>⚗️ Non-Hazardous Alternatives to Hazardous Acids</h4>
                <p>Ditch corrosive and hazardous acids like Sulphuric Acid and Hydrochloric Acid. Our eco-friendly descaling and surface preparation solutions offer powerful performance without dangerous fumes or corrosivity.</p>
              </motion.div>
              
              <motion.div 
                className="portfolio-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h4>🛡️ Safe Alternatives to Caustic & Heavy Alkali</h4>
                <p>Achieve efficient degreasing and cleaning without the risks of conventional caustic or heavy alkali chemicals. Our gentle, equipment-friendly alternatives safeguard both your workforce and machinery.</p>
              </motion.div>
              
              <motion.div 
                className="portfolio-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h4>🔄 Eco-Safe Substitutes for Acetone, IPA, and Methanol</h4>
                <p>Maintain industrial cleanliness with confidence using safe, effective replacements for common solvents like Acetone, Isopropyl Alcohol (IPA), and Methanol — reducing fire hazards and health risks.</p>
              </motion.div>
              
              <motion.div 
                className="portfolio-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h4>💧 Waterless Industrial Cleaners</h4>
                <p>Our innovative waterless cleaner reduces water usage while ensuring high cleaning performance, ideal for applications where water disposal or availability is a concern.</p>
              </motion.div>
              
              <motion.div 
                className="portfolio-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h4>🌊 Foamless Parts Cleaner</h4>
                <p>Enhance operational efficiency with our advanced foamless cleaner that simplifies rinsing processes and conserves water without compromising on cleaning power.</p>
              </motion.div>
              
              <motion.div 
                className="portfolio-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h4>🌡️ Green Chemistry-Based Cleaners (No Heating Required)</h4>
                <p>Cut energy costs with our revolutionary green chemistry cleaner designed to deliver effective cleaning at ambient temperatures — eliminating the need for heating systems.</p>
              </motion.div>
              
              <motion.div 
                className="portfolio-item"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h4>⚙️ Custom-Formulated Cleaning Solutions</h4>
                <p>Have a unique cleaning requirement? Our technical experts will collaborate to develop tailor-made eco-friendly solutions for your specific industrial cleaning challenges.</p>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.2 }}
          >
            <h3>Key Technical Advantages</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🛡️</span>
              </div>
              <div className="section-content">
                <h4>Human & Occupational Safety</h4>
                <ul>
                  <li>Non-hazardous & Non-carcinogenic – eliminates health risks inherent in traditional chemicals</li>
                  <li>Skin-safe, glove-optional use – no dermal toxicity or irritation</li>
                  <li>Odorless, no mist formation – supports clean, air-conditioned shopfloor environments</li>
                  <li>VOC-free & Non-flammable – safe for enclosed industrial spaces and export applications</li>
                  <li>No hazardous air pollutants (HAPs) – naturally meets regulatory compliance without air control systems</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.3 }}
          >
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">⚡</span>
              </div>
              <div className="section-content">
                <h4>Operational & Cleaning Performance</h4>
                <ul>
                  <li>Superior degreasing and cleaning efficiency – without heating, reducing energy consumption</li>
                  <li>Compatible with existing cleaning systems – no plant modification required</li>
                  <li>No rust promotion or staining on metals – ensures surface integrity</li>
                  <li>Residue-free, non-depositing finish – eliminates post-cleaning surface defects</li>
                  <li>Highly stable formulations – long shelf life and pot life, no separation or degradation</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.4 }}
          >
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">💧</span>
              </div>
              <div className="section-content">
                <h4>Resource & Consumption Efficiency</h4>
                <ul>
                  <li>Reduced chemical consumption per job – maximized operational cost efficiency</li>
                  <li>Minimal or no water required – near-zero wastewater generation</li>
                  <li>No requirement for dryers or heating equipment – further lowering energy and infrastructure expenses</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.5 }}
          >
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">📊</span>
              </div>
              <div className="section-content">
                <h4>Financial & Compliance Benefits</h4>
                <ul>
                  <li>Cuts safety infrastructure costs – no safety officer or PPE mandates</li>
                  <li>No air and effluent treatment plant (ETP) requirements – complies naturally with pollution norms</li>
                  <li>Boosts shopfloor productivity – improved ergonomics and faster turnaround</li>
                  <li>Supports Green Certifications and ESG mandates – enhances corporate sustainability credentials</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.6 }}
          >
            <h3>Our Commitment to Responsible Chemistry</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🌿</span>
              </div>
              <div className="section-content">
                <p>Every product we design reflects our unwavering commitment to Green Chemistry's 12 principles, promoting safer, cleaner, and economically viable industrial processes. We actively collaborate with industries to custom-develop alternatives for their specific cleaning challenges, ensuring seamless replacement of conventional hazardous chemicals without operational disruption.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.7 }}
          >
            <h3>Typical Applications</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🏭</span>
              </div>
              <div className="section-content">
                <ul>
                  <li>Metal Component Cleaning & Degreasing</li>
                  <li>Surface Pre-treatment for Painting & Coating</li>
                  <li>Rust Removal & Descaling</li>
                  <li>Electrical & Electronics Cleaning</li>
                  <li>Precision Cleaning in Pharma & Medical Devices</li>
                  <li>Oil & Grease Removal in Maintenance Operations</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.8 }}
          >
            <h3>Conclusion</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🌎</span>
              </div>
              <div className="section-content">
                <p>Through our Advanced Green Chemistry SBU, we are proud to offer sustainable industrial cleaning solutions that not only enhance operational efficiency but also safeguard human health and the environment. Our mission is to continuously innovate safe, high-performance chemical alternatives that help industries transition towards cleaner, greener, and safer manufacturing ecosystems.</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="cta-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
          >
            <h3>Ready to Transform Your Industrial Cleaning Process?</h3>
            <p>Discover how our eco-friendly solutions can improve safety, compliance, and efficiency in your operations.</p>
            <button className="cta-button">Schedule a Green Chemistry Consultation</button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default EcoFriendlySolutions; 